import React, { useEffect, useState, useRef } from "react";
import Notify from "../assets/navbar-notifi.svg";
import { useNavigate, Link } from "react-router-dom";
import { io } from "socket.io-client";
import { useGetNotificationsQuery, useMarkNotificationAsReadMutation, useLogoutMutation, authApi } from "../store/api";
import { logout as logoutAction } from "../store/slices/authSlice";
import { useDispatch } from "react-redux";
import { Menu, Settings, LogOut } from "lucide-react";
import LogoutModal from "./modals/LogoutModal";

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || "https://nodebackend.mapsims.store";
let socketInstance = null;

const getSocket = () => {
  if (!socketInstance) {
    socketInstance = io(SOCKET_URL, {
      withCredentials: true,
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });
  }

  return socketInstance;
};


export default function Navbar({ toggleSidebar }) {
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationsList, setNotificationsList] = useState([]);
  const notificationRef = useRef(null);
  
  const dispatch = useDispatch();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileRef = useRef(null);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [logoutApi, { isLoading: isLoggingOut }] = useLogoutMutation();

  useEffect(() => {
    function handleClickOutside(event) {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    }
    if (showNotifications || showProfileMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNotifications, showProfileMenu]);

  const userData = localStorage.getItem("user");

  const user =
    userData && userData !== "undefined"
      ? JSON.parse(userData)
      : null;

  const userName = user?.name || "User";
  const profileImage = user?.profilePicture?.secure_url;
  const { data: notificationsData, refetch: refetchNotifications } = useGetNotificationsQuery({ page: 1, limit: 10 });
  const [markNotificationAsRead] = useMarkNotificationAsReadMutation();
  const notifications = notificationsList.length > 0 ? notificationsList : notificationsData?.data?.notifications || [];

  useEffect(() => {
    if (!user) return undefined;

    const socket = getSocket();

    const handleNewNotification = (payload) => {
      const incoming = payload?.notification || payload;
      const normalizedNotification = {
        id: incoming?.id || incoming?._id || incoming?.notificationId,
        title: incoming?.title || incoming?.message || "New notification",
        message: incoming?.message || incoming?.body || incoming?.description || "",
        createdAt: incoming?.createdAt || incoming?.created_at || new Date().toISOString(),
        status: incoming?.status || incoming?.readStatus || "unread",
      };

      if (!normalizedNotification.id) return;

      setNotificationsList((prev) => {
        const exists = prev.some((item) => item.id === normalizedNotification.id);
        if (exists) {
          return prev.map((item) =>
            item.id === normalizedNotification.id ? { ...item, ...normalizedNotification } : item
          );
        }

        return [normalizedNotification, ...prev];
      });
    };

    socket.on("notification", handleNewNotification);

    return () => {
      socket.off("notification", handleNewNotification);
    };
  }, [user]);

  useEffect(() => {
    if (notificationsData?.data?.notifications) {
      setNotificationsList(notificationsData.data.notifications);
    }
  }, [notificationsData]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId).unwrap();
      
      if (notificationId) {
        setNotificationsList((prev) =>
          prev.map((item) => (item.id === notificationId ? { ...item, status: "read" } : item))
        );
      } else {
        setNotificationsList((prev) =>
          prev.map((item) => ({ ...item, status: "read" }))
        );
      }
      
      await refetchNotifications();
    } catch (error) {
      console.error("Failed to mark notification as read", error);
    }
  };

  async function handleConfirmLogout() {
    try {
      await logoutApi().unwrap();
    } catch (error) {
      console.error("Logout failed", error);
    } finally {
      dispatch(logoutAction());
      dispatch(authApi.util.resetApiState());
      setLogoutOpen(false);
      navigate("/account/login", { replace: true });
    }
  }

  return (
    <header className="px-4 lg:px-[26px] h-20 lg:h-[88px] bg-white flex-shrink-0 sticky top-0 z-20 flex items-center justify-between shadow-[0_1px_4px_0_#00000040]">
      <div className="flex items-center gap-3">
        <button 
          onClick={toggleSidebar} 
          className="md:hidden p-2 -ml-2 text-black hover:bg-gray-100 rounded-lg"
          aria-label="Open sidebar"
        >
          <Menu size={24} />
        </button>
        <div>
          <div className="text-sm lg:text-base text-[#878686]">Welcome back,</div>
          <div className="font-medium text-lg lg:text-xl leading-6 text-black">
            {userName}
          </div>
        </div>
      </div>

      <div className="xl:pr-6 flex items-center gap-5 lg:gap-7 divide-black">
        <div className="relative" ref={notificationRef}>
          <button
            aria-label="Notifications"
            onClick={() => setShowNotifications((prev) => !prev)}
            className="relative"
          >
            <img src={Notify} alt="notification icon" />
            {notifications.some((item) => item.status === "unread") && (
              <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#6D4E8A] text-[10px] font-semibold text-white">
                {notifications.filter((item) => item.status === "unread").length}
              </span>
            )}
          </button>

          {showNotifications && (
            <div className="absolute right-0 mt-3 w-80 max-h-96 overflow-y-auto rounded-xl border border-gray-200 bg-white p-3 shadow-xl z-30">
              <div className="mb-2 flex items-center justify-between px-1">
                <div className="text-sm font-semibold text-gray-800">Notifications</div>
                <button
                  type="button"
                  onClick={() => {
                    handleMarkAsRead();
                  }}
                  className="text-xs text-[#6D4E8A] hover:underline"
                >
                  Mark All Read
                </button>
              </div>
              {notifications.length > 0 ? (
                notifications.map((item) => (
                  <div 
                    key={item.id} 
                    onClick={() => {
                      if (item.status === "unread") {
                        handleMarkAsRead(item.id);
                      }
                    }}
                    className={`rounded-lg border px-3 py-2.5 mb-2 transition-colors ${item.status === 'unread' ? 'cursor-pointer border-[#6D4E8A] bg-[#f4f0f8]' : 'border-gray-100 bg-gray-50'}`}
                  >
                    <div>
                      <div className="text-sm font-medium text-black">{item.title}</div>
                      <div className="text-sm text-gray-600">{item.message}</div>
                      <div className="mt-1 text-xs text-gray-400">
                        {new Date(item.createdAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-1 py-3 text-sm text-gray-500">No notifications yet.</div>
              )}
              <div className="mt-2 border-t pt-2 text-center">
                <Link
                  to="/notifications"
                  onClick={() => setShowNotifications(false)}
                  className="text-sm font-semibold text-[#1FA5DE] hover:underline block w-full text-center"
                >
                  View All Notifications
                </Link>
              </div>
            </div>
          )}
        </div>
        <div className="w-px h-12 lg:h-14 bg-[#C3C3C3]"></div>
        <div className="relative" ref={profileRef}>
          <button
            onClick={() => setShowProfileMenu((prev) => !prev)}
            className="rounded-full overflow-hidden border-2 border-transparent hover:border-[#6D4E8A] transition-all"
          >
            {profileImage ? (
              <img
                src={profileImage}
                alt={userName}
                className="w-10 lg:w-12 h-10 lg:h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 lg:w-12 h-10 lg:h-12 rounded-full bg-[#6D4E8A] text-white text-3xl lg:text-4xl leading-6 flex items-center justify-center font-bold">
                {userName.charAt(0).toUpperCase()}
              </div>
            )}
          </button>
          
          {showProfileMenu && (
            <div className="absolute right-0 mt-3 w-48 bg-white border border-gray-200 rounded-xl shadow-xl z-30 py-2">
              <div className="px-4 py-2 border-b border-gray-100 mb-1">
                <p className="text-sm font-semibold text-black truncate">{userName}</p>
              </div>
              <button
                onClick={() => {
                  setShowProfileMenu(false);
                  setLogoutOpen(true);
                }}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
              >
                <LogOut size={16} />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
      
      <LogoutModal
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        onConfirm={handleConfirmLogout}
        isLoading={isLoggingOut}
      />
    </header>
  );
}
